import React, { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { formatDateTime } from '../utils.js';
import { fetchLeadNotifications } from '../api/notifications.js';
import { LeadNotificationsList } from './LeadNotificationsList.jsx';
import { EmptyState } from './ds';

export function LeadDetailsJournalTab({
    panelTab,
    lead,
    logScrollRef,
    activityItems,
    activityLoading,
    activityError,
    orderedActivityItems,
    managers,
    statusLookup,
}) {
    const isActive = panelTab === 'journal';
    const { data: notifData, isLoading: notifLoading } = useQuery({
        queryKey: ['notifications', 'lead', lead?.id],
        queryFn: () => fetchLeadNotifications(lead.id),
        enabled: isActive && Boolean(lead?.id),
    });
    const notifications = notifData?.notifications ?? [];
    const numberFormatter = useMemo(() => new Intl.NumberFormat('ru-RU'), []);
    const formatCurrency = (value) => numberFormatter.format(value);
    const managerLookup = useMemo(() => {
        return new Map((managers || []).map(manager => [manager.id, manager]));
    }, [managers]);

    const resolveActor = (createdBy) => {
        if (!createdBy || createdBy === 'system') return 'Система';
        const manager = managerLookup.get(createdBy);
        if (manager) {
            return manager.name || manager.email || 'Менеджер';
        }
        return createdBy;
    };

    const formatStatusLabel = (statusId) => {
        if (!statusId) return '—';
        if (statusLookup?.get(statusId)) {
            return statusLookup.get(statusId).label;
        }
        return statusId;
    };

    const formatRangeLabel = (range) => {
        if (!range || typeof range !== 'object') return '—';
        const min = Number.isFinite(Number(range.min)) ? Number(range.min) : null;
        const max = Number.isFinite(Number(range.max)) ? Number(range.max) : null;
        if (min !== null && max !== null) {
            return `${formatCurrency(min)} — ${formatCurrency(max)}`;
        }
        if (min !== null) return `от ${formatCurrency(min)}`;
        if (max !== null) return `до ${formatCurrency(max)}`;
        return '—';
    };

    const activityTone = (type) => {
        const t = String(type || '');
        if (/^status_/.test(t)) return 'status';
        if (/^(email_|reply_|pushup_|offer_)/i.test(t) || t === 'ENROLLMENT_REMOVED') return 'email';
        return 'neutral';
    };

    const formatActivityType = (type) => {
        if (activityTone(type) === 'email') return 'Email';
        if (activityTone(type) === 'status') return 'Статус';
        switch (type) {
            case 'status_change':
                return 'Статус';
            case 'owner_change':
            case 'owner_changed':
                return 'Менеджер';
            case 'negotiation_range':
                return 'Торг';
            case 'field_change':
                return 'Изменение';
            case 'duplicate_collapsed':
                return 'Дубль влит';
            default:
                return 'Событие';
        }
    };

    const resolveActivitySource = (activity) => {
        const origin = activity?.payload?.origin || activity?.createdBy;
        if (!origin || origin === 'system' || String(origin).startsWith('wazzup')) {
            return { type: 'system', badge: 'SYS' };
        }
        return { type: 'human', badge: 'HUM' };
    };

    const formatFieldValue = (value) => {
        if (value === null || value === undefined || value === '') return '—';
        if (typeof value === 'object') {
            try {
                return JSON.stringify(value);
            } catch {
                return String(value);
            }
        }
        return String(value);
    };

    const renderActivityBody = (activity) => {
        const { payload, description } = activity || {};
        switch (activity?.type) {
            case 'status_change': {
                const from = formatStatusLabel(payload?.from);
                const to = formatStatusLabel(payload?.to);
                return (
                    <>
                        <div className='oc-lead-card__activity-diff'>
                            <span>{from}</span>
                            <span className='oc-lead-card__activity-sep'>→</span>
                            <span>{to}</span>
                        </div>
                        {payload?.decision?.knowledgeSnippet && (
                            <div className='oc-lead-card__activity-note'>{payload.decision.knowledgeSnippet}</div>
                        )}
                        {description && <div className='oc-lead-card__activity-note'>{description}</div>}
                    </>
                );
            }
            case 'owner_change': {
                const from = payload?.from ? resolveActor(payload.from) : '—';
                const to = payload?.to ? resolveActor(payload.to) : '—';
                return (
                    <>
                        <div className='oc-lead-card__activity-diff'>
                            <span>{from}</span>
                            <span className='oc-lead-card__activity-sep'>→</span>
                            <span>{to}</span>
                        </div>
                        {description && <div className='oc-lead-card__activity-note'>{description}</div>}
                    </>
                );
            }
            case 'negotiation_range': {
                const from = formatRangeLabel(payload?.from);
                const to = formatRangeLabel(payload?.to);
                return (
                    <>
                        <div className='oc-lead-card__activity-diff'>
                            <span>{from}</span>
                            <span className='oc-lead-card__activity-sep'>→</span>
                            <span>{to}</span>
                        </div>
                        {description && <div className='oc-lead-card__activity-note'>{description}</div>}
                    </>
                );
            }
            case 'field_change': {
                return (
                    <>
                        <div className='oc-lead-card__activity-note'>
                            {payload?.label || 'Поле'}: {formatFieldValue(payload?.to)}
                        </div>
                        {description && <div className='oc-lead-card__activity-note'>{description}</div>}
                    </>
                );
            }
            default:
                return description ? <div className='oc-lead-card__activity-note'>{description}</div> : null;
        }
    };

    return (
        <div className='details-modal__chat oc-lead-card__journal' style={{ display: panelTab === 'journal' ? 'flex' : 'none' }}>
            <div className='oc-lead-card__log'>
                <header className='oc-lead-card__log-head'>
                    <h3 className='oc-lead-card__log-title'>Журнал</h3>
                    <span className='oc-lead-card__log-count'>{orderedActivityItems.length}</span>
                </header>
                <div ref={logScrollRef} className='oc-lead-card__log-list'>
                    {activityLoading ? (
                        <EmptyState compact>Загружаем…</EmptyState>
                    ) : activityError ? (
                        <EmptyState compact tone='error'>{activityError}</EmptyState>
                    ) : activityItems.length === 0 ? (
                        <EmptyState compact>Журнал пуст</EmptyState>
                    ) : (
                        orderedActivityItems.map(item => {
                            const source = resolveActivitySource(item);
                            return (
                                <div key={item.id || `${item.type}-${item.createdAt}`} className='oc-lead-card__activity'>
                                    <div className='oc-lead-card__activity-meta'>
                                        <span className={`oc-lead-card__activity-dot oc-lead-card__activity-dot--${source.type}`}>
                                            {source.badge}
                                        </span>
                                        <span className='oc-lead-card__activity-author'>{resolveActor(item.createdBy)}</span>
                                        <span className={`oc-lead-card__activity-channel oc-lead-card__activity-channel--${activityTone(item.type)}`}>{formatActivityType(item.type)}</span>
                                        <span className='oc-lead-card__activity-time'>{formatDateTime(item.createdAt)}</span>
                                    </div>
                                    <div className='oc-lead-card__activity-body'>
                                        {renderActivityBody(item)}
                                    </div>
                                </div>
                            );
                        })
                    )}
                </div>
            </div>

            <div className='oc-lead-card__log'>
                <header className='oc-lead-card__log-head'>
                    <h3 className='oc-lead-card__log-title'>Уведомления</h3>
                    <span className='oc-lead-card__log-count'>{notifications.length}</span>
                </header>
                <LeadNotificationsList notifications={notifications} loading={notifLoading} />
            </div>
        </div>
    );
}
